import React from "react"

import { Alert } from "../alert/Alert"
import { CustomImage } from "../image/CustomImage"
import { Image } from "../image/Image"
import { LocalizedText } from "../text/LocalizedText"
import { FlexLayout } from "../layout/flex/FlexLayout"
import { TextLayout } from "../layout/text/TextLayout"
import { Button } from "../button/Button"
import { DateInput } from "../form/DateInput"
import { DangerousProps } from "../helpers/DangerousProps"
import { TestProps } from "../helpers/TestProps"
import { renderTestID } from "../helpers/renderTestID"
import { DateExtraction } from "./DateExtraction"
import {
  DateOcrExtractionProvider,
  DateOcrExtractionResult,
} from "./DateOcrExtractionProvider"
import { DateExtractionType } from "./DateExtractionType"
import { CameraUnavailableReason, CaptureHotKeys } from "./ImageCapture"
import { PollingSessionMeta, TimerScheduler } from "./PollingSession"
import {
  DateAuditEvent,
  DateCaptureOutcome,
  DateCaptureResult,
  DateCaptureStatus,
  toAuditFailureReason,
} from "./DateAuditEvent"

export interface DateCaptureStrings {
  /**
   * Title used in the error Alert shown above the next OCR attempt and the
   * manual fallback. May use the `{reason}` MessageFormat placeholder.
   */
  failureAlertTitle?: string
  /**
   * Title used in the success Alert shown on the confirmation screen.
   * May use the `{date}` MessageFormat placeholder.
   */
  successAlertTitle?: string
  /** Title used in the info Alert shown while OCR is running. */
  processingAlertTitle?: string
  /**
   * Instruction shown at the top of the initial extracting screen, before
   * the user has captured anything.
   */
  captureInstruction?: string
  /** Label for the "this looks right" button on the confirmation screen. */
  correct?: string
  /** Label for the "this looks wrong" button on the confirmation screen. */
  incorrect?: string
  /** Label for the submit button on the manual entry screen. */
  submit?: string
  /** Label for the control that returns from manual entry to the camera. */
  captureAgain?: string
}

export interface DateCaptureProps extends DangerousProps, TestProps {
  /** Form field name (result submitted as ${name}$json). */
  name: string
  /**
   * OCR provider used to extract a date from the captured image.
   * If not provided, must be injected by the container at runtime.
   */
  ocrProvider?: DateOcrExtractionProvider
  /** Type of date to extract (default: "expirationDate"). */
  extractionType?: DateExtractionType
  /**
   * Output image format for the captured image (default: "image/jpeg").
   *
   * JPEG is lossy but small, keeping request payloads well within backend
   * size limits. PNG is lossless and preserves sharp text edges for OCR
   * accuracy, at the cost of a much larger payload.
   */
  imageType?: "image/jpeg" | "image/png"
  /**
   * JPEG compression quality from 0.0 to 1.0 (default: 1.0). Only applies when
   * `imageType` is "image/jpeg"; ignored for PNG, which is lossless. Higher
   * values preserve more detail for OCR at the cost of a larger payload.
   */
  imageQuality?: number
  /** Maximum width for captured image (default: 1280). */
  maxWidth?: number
  /** Maximum height for captured image (default: 720). */
  maxHeight?: number
  /** Automatically open camera on mount (default: false). */
  autoOpenCamera?: boolean
  /**
   * Hot keys for the capture controls. See CaptureHotKeys. No defaults: the
   * view picks the keys, because conflicts depend on the whole screen.
   */
  hotKeys?: CaptureHotKeys
  /**
   * Links the captured image to an audit record for backend persistence.
   * Passed through to the OCR provider via DateExtraction. Required by
   * AFTConanService, today's only OCR backend; another may not need it.
   */
  auditId: string
  /**
   * Scannable identifier of the container being processed.
   * Passed through to the OCR provider via DateExtraction.
   */
  containerScannableId: string
  /** FcSku of the item. At least one of fcSku or fnSku must be provided. */
  fcSku?: string
  /** FnSku of the item. At least one of fcSku or fnSku must be provided. */
  fnSku?: string
  /**
   * Treat a camera as a hard requirement (default: false).
   *
   * When set, the session ends with status CAMERA_UNAVAILABLE instead of
   * falling back to the file picker and manual entry, in either of two cases:
   * detection finds no camera, which is known on mount; or the browser refuses
   * to open the one it found, which is only known once capture is attempted.
   * The recorded skipReason distinguishes them.
   *
   * Applies on every device type: detection runs whichever surface is in use.
   */
  enforceCamera?: boolean
  /** Maximum OCR retries before falling back to manual (default: 0). */
  maxRetries?: number
  /** Disable automatic form submission (default: false). */
  disableAutoSubmit?: boolean
  /**
   * Poll the live viewfinder automatically instead of waiting for a Capture
   * and Confirm gesture per attempt (default: false). Only honoured when a
   * camera could be opened.
   *
   * Implies the in-app viewfinder, which is the only source of live frames.
   * Single-shot capture keeps the device's own camera until the overlay
   * behaviour is settled.
   */
  autoCapture?: boolean
  /**
   * Minimum gap in milliseconds between the settlement of one poll request and
   * the issue of the next (default: DEFAULT_POLL_INTERVAL_MS).
   */
  pollIntervalMs?: number
  /**
   * Maximum poll requests in a single polling session
   * (default: DEFAULT_MAX_POLL_ATTEMPTS).
   */
  maxPollAttempts?: number
  /**
   * Maximum wall-clock duration of a single polling session, in milliseconds
   * (default: DEFAULT_POLL_TIMEOUT_MS).
   */
  pollTimeoutMs?: number
  /** Maximum width for polled frames. Falls back to `maxWidth` when omitted. */
  pollMaxWidth?: number
  /**
   * Maximum height for polled frames. Falls back to `maxHeight` when omitted.
   */
  pollMaxHeight?: number
  /**
   * Deferred-callback pair used to pace the polling loop. Injected by tests so
   * they advance simulated time instead of waiting on real elapsed time.
   */
  timerScheduler?: TimerScheduler
  /** Minimum year for manual DateInput validation. */
  minYear?: number
  /** Maximum year for manual DateInput validation. */
  maxYear?: number
}

type Phase = "extracting" | "confirming" | "manual"

interface CapturedImage {
  base64: string
  imageType: "image/jpeg" | "image/png"
}

interface DateCaptureState {
  phase: Phase
  ocrResult: DateOcrExtractionResult | null
  ocrResultMeta?: PollingSessionMeta
  capturedImage: CapturedImage | null
  isProcessing: boolean
  retryCount: number
  manualDate: string
  auditEvents: DateAuditEvent[]
  outputJson: string
}

/**
 * DateCapture orchestrates a full date capture workflow:
 * 1. Captures an image and runs OCR via DateExtraction
 * 2. On success: shows the extracted date for user confirmation
 * 3. On failure or rejection: retries or falls back to manual DateInput
 *
 * Throughout the session DateCapture accumulates audit events (image
 * captures, OCR results, confirmations, manual entries). The final output,
 * written to a hidden form input as JSON, is a DateCaptureResult containing
 * the ordered events plus an outcome describing the final selected date and
 * its source. The host application is responsible for the RecordAudit call;
 * the outcome lets backends know the resolved date without reconstructing it
 * from the event list.
 *
 * The parent form is auto-submitted (unless disableAutoSubmit=true).
 */
export class DateCapture extends React.PureComponent<
  DateCaptureProps,
  DateCaptureState
> {

  public static defaultProps: Partial<DateCaptureProps> = {
    extractionType: "expirationDate",
    imageType: "image/jpeg",
    imageQuality: 1.0,
    enforceCamera: false,
    maxRetries: 0,
    disableAutoSubmit: false,
  }

  /** Override to customize strings or provide translations. */
  public static defaultStrings?: DateCaptureStrings

  private readonly hiddenInputRef = React.createRef<HTMLInputElement>()

  // Stable empty fallback so render references don't churn.
  private readonly emptyStrings: DateCaptureStrings = {}

  public constructor(props: DateCaptureProps) {
    super(props)
    this.state = {
      phase: "extracting",
      ocrResult: null,
      capturedImage: null,
      isProcessing: false,
      retryCount: 0,
      manualDate: "",
      auditEvents: [],
      outputJson: "",
    }
  }

  private appendAuditEvent(event: DateAuditEvent) {
    this.setState((prevState) => ({
      auditEvents: [...prevState.auditEvents, event],
    }))
  }

  /**
   * Poll fields for an audit event context
   */
  private pollFields(meta?: PollingSessionMeta): {
    pollAttemptCount?: number
    autoCaptureUnavailable?: boolean
  } {
    if (!meta) {
      return {}
    }
    return {
      ...(meta.pollAttemptCount !== undefined
        ? { pollAttemptCount: meta.pollAttemptCount }
        : {}),
      ...(meta.autoCaptureUnavailable
        ? { autoCaptureUnavailable: true }
        : {}),
    }
  }

  private handleImageCaptured = (
    base64Image: string,
    imageType: "image/jpeg" | "image/png",
    meta?: PollingSessionMeta
  ) => {
    this.setState({
      capturedImage: { base64: base64Image, imageType },
      isProcessing: true,
    })
    const fields = this.pollFields(meta)
    this.appendAuditEvent({
      eventTimestamp: Date.now(),
      eventType: "IMAGE_CAPTURE",
      ...(Object.keys(fields).length > 0 ? { eventContext: fields } : {}),
    })
  }

  private handleExtractionResult = (
    result: DateOcrExtractionResult,
    meta?: PollingSessionMeta
  ) => {
    this.setState({ isProcessing: false })
    this.recordOcrEvent(result, meta)

    if (result.success && result.extractedDate) {
      this.setState({
        phase: "confirming",
        ocrResult: result,
        ocrResultMeta: meta,
      })
      return
    }

    if (meta?.pollAttemptCount !== undefined) {
      /*
       * An exhausted polling session goes straight to manual entry
       */
      this.setState({ phase: "manual", ocrResult: result })
      return
    }

    this.handleNegativeResult(result)
  }

  /**
   * Synthesizes an audit event from the OCR extraction response and appends
   * it to the event list on the user's behalf.
   */
  private recordOcrEvent(
    result: DateOcrExtractionResult,
    meta?: PollingSessionMeta
  ) {
    const eventTimestamp = Date.now()
    // All three contexts below already exist unconditionally, so spreading an
    // empty fragment into them is a no-op.
    const pollFields = this.pollFields(meta)

    if (result.success && result.extractedDate) {
      this.appendAuditEvent({
        eventTimestamp,
        eventType: "OCR_DATE_CAPTURE",
        eventContext: {
          date: result.extractedDate,
          confidence: result.confidence,
          rawDateText: result.originalDateText,
          imageUri: result.imageUri,
          ...pollFields,
        },
      })
    } else if (result.failureReason === "NO_DATE_VISIBLE") {
      this.appendAuditEvent({
        eventTimestamp,
        eventType: "NO_DATE_DETECTED",
        eventContext: {
          imageUri: result.imageUri,
          ...pollFields,
        },
      })
    } else {
      this.appendAuditEvent({
        eventTimestamp,
        eventType: "OCR_FAILURE",
        eventContext: {
          failureReason: toAuditFailureReason(result.failureReason),
          errorMessage: result.errorMessage,
          imageUri: result.imageUri,
          ...pollFields,
        },
      })
    }
  }

  private handleNegativeResult(result: DateOcrExtractionResult) {
    const { maxRetries = 0 } = this.props
    const retryCount = this.state.retryCount + 1

    if (retryCount > maxRetries) {
      this.setState({ phase: "manual", ocrResult: result, retryCount })
    } else {
      // Go back to extracting for another attempt
      this.setState({
        phase: "extracting",
        ocrResult: result,
        retryCount,
      })
    }
  }

  private handleCorrect = () => {
    const { ocrResult } = this.state
    if (!ocrResult || !ocrResult.extractedDate) {
      return
    }

    this.appendAuditEvent({
      eventTimestamp: Date.now(),
      eventType: "USER_CONFIRMATION",
      eventContext: {
        confirmedValue: ocrResult.extractedDate,
        confirmationMethod: "YES_NO",
      },
    })
    this.complete("SUCCESS", {
      selectedDate: ocrResult.extractedDate,
      capturedDate: ocrResult.extractedDate,
      capturedDateSource: "OCR",
      dateType: this.props.extractionType ?? "expirationDate",
      yearComponent: ocrResult.yearComponent,
      monthComponent: ocrResult.monthComponent,
      dayComponent: ocrResult.dayComponent,
    })
  }

  /**
   * The worker rejected the extracted date.
   */
  private handleIncorrect = () => {
    const rejected: DateOcrExtractionResult = {
      success: false,
      failureReason: "USER_REJECTED",
    }

    if (this.state.ocrResultMeta?.pollAttemptCount !== undefined) {
      this.setState({ phase: "manual", ocrResult: null })
      return
    }

    this.handleNegativeResult(rejected)
  }

  private handleManualDateChange = (newValue: string) => {
    this.setState({ manualDate: newValue })
  }

  private handleManualEntryRequested = (meta?: PollingSessionMeta) => {
    this.appendAuditEvent({
      eventTimestamp: Date.now(),
      eventType: "USER_SKIP",
      eventContext: {
        skipReason: "ASSOCIATE_SKIPPED",
        ...this.pollFields(meta),
      },
    })
    this.setState({ phase: "manual" })
  }

  private handleReturnToCapture = () => {
    this.setState({ phase: "extracting" })
  }

  /**
   * No camera on a device where the caller requires one. Ends the session
   * rather than offering the file picker or manual entry, recording why so the
   * backend can tell a missing camera apart from a worker who chose to skip,
   * and an absent camera apart from one the browser would not open.
   */
  private handleCameraUnavailable = (reason: CameraUnavailableReason) => {
    this.appendAuditEvent({
      eventTimestamp: Date.now(),
      eventType: "USER_SKIP",
      eventContext: { skipReason: reason },
    })
    this.complete("CAMERA_UNAVAILABLE")
  }

  private handleManualSubmit = () => {
    const { manualDate } = this.state
    if (!manualDate) {
      return
    }

    const [yearComponent, monthComponent, dayComponent] = manualDate.split("-")
    this.appendAuditEvent({
      eventTimestamp: Date.now(),
      eventType: "USER_DATE_ENTRY",
      eventContext: {
        date: manualDate,
      },
    })
    this.complete("SUCCESS", {
      selectedDate: manualDate,
      capturedDate: manualDate,
      capturedDateSource: "USER_ENTERED",
      dateType: this.props.extractionType ?? "expirationDate",
      yearComponent,
      monthComponent,
      dayComponent,
    })
  }

  /**
   * Finalizes the capture session: serializes the status, the accumulated audit
   * events and the outcome to the hidden input, then submits the form (unless
   * disabled).
   *
   * The only writer of the serialized result, so status and outcome cannot
   * drift out of step. `outcome` is omitted entirely for any status other than
   * SUCCESS — JSON.stringify drops the undefined key.
   */
  private complete(status: DateCaptureStatus, outcome?: DateCaptureOutcome) {
    this.setState((prevState) => {
      const result: DateCaptureResult = {
        status,
        events: prevState.auditEvents,
        outcome,
      }
      return { outputJson: JSON.stringify(result) }
    }, () => {
      if (!this.props.disableAutoSubmit) {
        this.submitForm()
      }
    })
  }

  private submitForm = () => {
    const formElement = this.hiddenInputRef.current?.form
    if (formElement) {
      formElement.requestSubmit()
    }
  }

  private renderCapturedImage() {
    const { capturedImage } = this.state
    if (!capturedImage) {
      return <Image name="capture-product" altText=""/>
    }
    return (
      <CustomImage
        url={`data:${capturedImage.imageType};base64,${capturedImage.base64}`}
        altText={{ defaultString: "Captured image" }}
        spacing="none"
        dangerousClassName="date-capture__captured-image"
      />
    )
  }

  private renderFailureAlert() {
    const { ocrResult } = this.state
    if (!ocrResult || !ocrResult.failureReason) {
      return null
    }
    const strings = DateCapture.defaultStrings || this.emptyStrings
    // TODO: map failureReason to a friendly per-reason message instead of the
    // raw enum value.
    return (
      <Alert
        variant="error"
        title={{
          stringID: strings.failureAlertTitle,
          defaultString: "Previous attempt failed: {reason}",
          parameters: { reason: ocrResult.failureReason },
        }}
      />
    )
  }

  private renderProcessingAlert() {
    const strings = DateCapture.defaultStrings || this.emptyStrings
    return (
      <Alert
        variant="info"
        title={{
          stringID: strings.processingAlertTitle,
          defaultString: "Processing image...",
        }}
      />
    )
  }

  private renderCaptureInstruction() {
    const strings = DateCapture.defaultStrings || this.emptyStrings
    return (
      <TextLayout vAlign="middle" hAlign="center">
        <LocalizedText
          stringID={strings.captureInstruction}
          defaultString="Capture product expiration date"
          variant="bright-blue"
          size="xl"
          attributed={true}
        />
      </TextLayout>
    )
  }

  private renderExtracting() {
    const {
      ocrProvider, extractionType, imageType, imageQuality, auditId,
      containerScannableId, fcSku, fnSku, maxWidth, maxHeight, autoOpenCamera,
      autoCapture, pollIntervalMs, maxPollAttempts, pollTimeoutMs,
      pollMaxWidth, pollMaxHeight, timerScheduler, enforceCamera, hotKeys,
    } = this.props
    const { retryCount, isProcessing } = this.state
    const { capturedImage } = this.state
    return (
      <FlexLayout direction="column" key="extracting">
        <FlexLayout.Item size="auto" padding={capturedImage ? 0 : "md"}>
          {capturedImage
            ? (isProcessing
              ? this.renderProcessingAlert()
              : this.renderFailureAlert())
            : this.renderCaptureInstruction()}
        </FlexLayout.Item>
        <FlexLayout.Item size="1fr" padding={0}>
          {this.renderCapturedImage()}
        </FlexLayout.Item>
        <FlexLayout.Item size="auto" padding={0}>
          <DateExtraction
            key={retryCount}
            name={`${this.props.name}__extraction`}
            ocrProvider={ocrProvider}
            extractionType={extractionType}
            imageType={imageType}
            imageQuality={imageQuality}
            auditId={auditId}
            containerScannableId={containerScannableId}
            fcSku={fcSku}
            fnSku={fnSku}
            maxWidth={maxWidth}
            maxHeight={maxHeight}
            autoOpenCamera={autoOpenCamera}
            hotKeys={hotKeys}
            autoCapture={autoCapture}
            pollIntervalMs={pollIntervalMs}
            maxPollAttempts={maxPollAttempts}
            pollTimeoutMs={pollTimeoutMs}
            pollMaxWidth={pollMaxWidth}
            pollMaxHeight={pollMaxHeight}
            timerScheduler={timerScheduler}
            disableAutoSubmit={true}
            onResult={this.handleExtractionResult}
            onImageCaptured={this.handleImageCaptured}
            onRequestManualEntry={this.handleManualEntryRequested}
            onCameraUnavailable={
              enforceCamera ? this.handleCameraUnavailable : undefined
            }
          />
        </FlexLayout.Item>
      </FlexLayout>
    )
  }

  private renderConfirming() {
    const { ocrResult } = this.state
    const strings = DateCapture.defaultStrings || this.emptyStrings
    // TODO: Consider using a pre-filled DateInput instead of plain text
    return (
      <FlexLayout direction="column" key="confirming">
        <FlexLayout.Item size="auto" padding={0}>
          <Alert
            variant="success"
            title={{
              stringID: strings.successAlertTitle,
              defaultString: "Date captured: {date}",
              parameters: { date: ocrResult?.extractedDate ?? "" },
            }}
          />
        </FlexLayout.Item>
        <FlexLayout.Item size="1fr" padding={0}>
          {this.renderCapturedImage()}
        </FlexLayout.Item>
        <FlexLayout.Item size="auto" padding={0}>
          <FlexLayout direction="row-ltr">
            <FlexLayout.Item size="1fr" padding={0}>
              <Button
                variant="primary"
                fluid={true}
                onClick={this.handleCorrect}
              >
                <LocalizedText
                  stringID={strings.correct}
                  defaultString="Correct"
                />
              </Button>
            </FlexLayout.Item>
            <FlexLayout.Item size="1fr" padding={0}>
              <Button
                variant="secondary"
                fluid={true}
                onClick={this.handleIncorrect}
              >
                <LocalizedText
                  stringID={strings.incorrect}
                  defaultString="Incorrect"
                />
              </Button>
            </FlexLayout.Item>
          </FlexLayout>
        </FlexLayout.Item>
      </FlexLayout>
    )
  }

  private renderManual() {
    const { name, minYear, maxYear } = this.props
    return (
      <FlexLayout direction="column" key="manual">
        <FlexLayout.Item size="auto" padding={0}>
          {this.renderFailureAlert()}
        </FlexLayout.Item>
        <FlexLayout.Item size="1fr" padding={0}>
          {this.renderCapturedImage()}
        </FlexLayout.Item>
        <FlexLayout.Item size="auto" padding={0}>
          <DateInput
            name={`${name}__manual`}
            minYear={minYear}
            maxYear={maxYear}
            onChange={this.handleManualDateChange}
            autoFocus={true}
          />
        </FlexLayout.Item>
        <FlexLayout.Item size="auto" padding={0}>
          <Button
            variant="primary"
            fluid={true}
            onClick={this.handleManualSubmit}
          >
            <LocalizedText
              stringID={
                (DateCapture.defaultStrings || this.emptyStrings).submit
              }
              defaultString="Submit"
            />
          </Button>
        </FlexLayout.Item>
        <FlexLayout.Item size="auto" padding={0}>
          <Button
            variant="secondary"
            fluid={true}
            onClick={this.handleReturnToCapture}
          >
            <LocalizedText
              stringID={
                (DateCapture.defaultStrings || this.emptyStrings).captureAgain
              }
              defaultString="Capture again"
            />
          </Button>
        </FlexLayout.Item>
      </FlexLayout>
    )
  }

  public render() {
    const { name, dangerousClassName, dangerousStyle, testID } = this.props
    const { phase, outputJson } = this.state

    const className = "date-capture"
      + (dangerousClassName ? ` ${dangerousClassName}` : "")

    // Claim full container height so the internal FlexLayout has bounded
    // space to distribute; callers keep control via dangerousStyle overrides.
    const style: React.CSSProperties = { height: "100%", ...dangerousStyle }

    return (
      <div
        className={className}
        style={style}
        data-testid={renderTestID(testID)}
      >
        {phase === "extracting" && this.renderExtracting()}
        {phase === "confirming" && this.renderConfirming()}
        {phase === "manual" && this.renderManual()}

        <input
          ref={this.hiddenInputRef}
          type="hidden"
          name={`${name}$json`}
          value={outputJson}
        />
      </div>
    )
  }
}
